import { network } from '@root/utils'
import { QueryFunction } from './root'

interface ConstantProps {
  slug: string
}

export type IVatCodeTag = 'sales' | 'purchase' | 'reverse_charge' | 'taxfree'
export interface IVatCode {
  label: string
  label_short: string
  info: string
  value: number
  tags: IVatCodeTag[]
}

export interface ICountry {
  code: string
  name: string
}

interface CountryListResponse {
  countries: ICountry[]
}

export const fetchVATCodes: QueryFunction<ConstantProps, IVatCode[]> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/vat_codes/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchCountries: QueryFunction<ConstantProps, ICountry[]> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/countries/`
  const { data } = await network.httpClient.request<CountryListResponse>({
    url,
    method: 'GET'
  })
  return data?.countries ?? []
}

export const fetchBusinessInvoicingCountries: QueryFunction<ConstantProps, ICountry[]> = async ({
  slug
}) => {
  const url = `/v1/business/${slug}/constants/business_invoicing_countries/`
  const { data } = await network.httpClient.request<CountryListResponse>({
    url,
    method: 'GET'
  })
  return data?.countries ?? []
}

export interface INocfoPermission {
  id: string
  name: string
  explanation: string
}

export interface INocfoPermissionGroup {
  id: string
  name: string
  explanation: string
  permissions: INocfoPermission[]
}

interface PermissionGroupProps {
  slug: string
}

export const fetchPermissionGroups: QueryFunction<
  PermissionGroupProps,
  INocfoPermissionGroup[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/permission_groups/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchPermissions: QueryFunction<PermissionGroupProps, INocfoPermission[]> = async ({
  slug
}) => {
  const url = `/v1/business/${slug}/constants/permissions/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const TOKEN_STORAGE_KEY = 'token'
export const MOBILE_PATH_PREFIX = '/mobile'

export interface IGeneralConstantOption {
  value: string
  label: string
  info: string
}

export const fetchVATReportingMethods: QueryFunction<
  ConstantProps,
  IGeneralConstantOption[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/vat_reporting_methods/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchVATPostingMethods: QueryFunction<
  ConstantProps,
  IGeneralConstantOption[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/vat_posting_methods/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchInvoicingLanguages: QueryFunction<
  ConstantProps,
  IGeneralConstantOption[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/invoicing_languages/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IBusinessFormOption {
  value: string
  label: string
}

export const fetchBusinessForms = async (market: string): Promise<IBusinessFormOption[]> => {
  const { data } = await network.httpClient.request({
    url: '/v1/constants/business_forms/',
    method: 'GET',
    headers: { 'X-NoCFO-Market': market }
  })
  return data
}

export interface IDocumentRelationRoleOption {
  value: string
  label: string
}

export const fetchDocumentRelationRoles: QueryFunction<
  ConstantProps,
  IDocumentRelationRoleOption[]
> = async ({ slug }) => {
  const url = `/v1/business/${slug}/constants/document_relation_roles/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IUsershipTag {
  id: string
  name: string
  plural: string
  description: string
}

export interface IUsershipTagHelp {
  title: string
  what: string
  access: string
  permissions: string
  workflows: string
}

export interface IUsershipTagCatalog {
  help: IUsershipTagHelp
  roles: IUsershipTag[]
}

export const fetchUsershipTags: QueryFunction<ConstantProps, IUsershipTagCatalog> = async ({
  slug
}) => {
  const url = `/v1/business/${slug}/constants/usership_tags/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}
